import * as React from "react";
import { cn } from "@/lib/utils";
import { Twitter, Linkedin, Github } from "lucide-react";

interface FooterLink {
  label: string;
  href: string;
}

interface FooterNavSection {
  title: string;
  links: FooterLink[];
}

interface SocialLink {
  platform: string;
  url: string;
}

interface FooterProps {
  company: {
    name: string;
    description: string;
    copyright: string;
  };
  navigation: FooterNavSection[];
  social?: SocialLink[];
}

const socialIcons: Record<string, React.ElementType> = {
  twitter: Twitter,
  linkedin: Linkedin,
  github: Github,
};

/**
 * Footer (Neo-Brutalist Version)
 *
 * Site footer with:
 * - Dark background with solid borders
 * - Company info with yellow accent logo
 * - Multi-column navigation
 * - Social links with hard shadows
 * - Copyright
 */
export function Footer({ company, navigation, social }: FooterProps) {
  return (
    <footer className="relative bg-[#1A1A1A] border-t-4 border-black">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-12 lg:gap-8">
          {/* Company info */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div
                className={cn(
                  "w-10 h-10 rounded-xl",
                  "bg-[#FFDC00]",
                  "border-3 border-black",
                  "shadow-[3px_3px_0_0_#000000]",
                  "flex items-center justify-center"
                )}
              >
                <span className="text-black font-black text-sm font-['Epilogue']">
                  BG
                </span>
              </div>
              <span className="text-white font-bold text-lg font-['Epilogue']">
                {company.name}
              </span>
            </div>
            <p className="text-white/70 text-sm leading-relaxed mb-6 max-w-xs font-['Sora']">
              {company.description}
            </p>

            {/* Social links */}
            {social && social.length > 0 && (
              <div className="flex items-center gap-3">
                {social.map((link) => {
                  const Icon = socialIcons[link.platform] || Github;
                  return (
                    <a
                      key={link.platform}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cn(
                        "w-10 h-10 rounded-xl flex items-center justify-center",
                        "bg-[#FFDC00] text-black",
                        "border-2 border-black",
                        "shadow-[3px_3px_0_0_#000000]",
                        "transition-all duration-150",
                        "hover:-translate-x-0.5 hover:-translate-y-0.5",
                        "hover:shadow-[4px_4px_0_0_#000000]",
                        "active:translate-x-0.5 active:translate-y-0.5 active:shadow-none"
                      )}
                      aria-label={`Follow us on ${link.platform}`}
                    >
                      <Icon className="w-5 h-5" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {/* Navigation columns */}
          {navigation.map((section) => (
            <div key={section.title}>
              <h4 className="text-white font-bold text-sm mb-4 font-['Epilogue'] uppercase tracking-wide">
                {section.title}
              </h4>
              <ul className="space-y-3">
                {section.links.map((link) => (
                  <li key={link.href}>
                    <a
                      href={link.href}
                      className={cn(
                        "text-white/70 text-sm font-['Sora']",
                        "hover:text-[#FFDC00]",
                        "transition-colors duration-150"
                      )}
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div
          className={cn(
            "mt-16 pt-8",
            "border-t-3 border-white/20",
            "flex flex-col sm:flex-row items-center justify-between gap-4"
          )}
        >
          <p className="text-white/50 text-sm font-['Sora']">
            {company.copyright}
          </p>
          <div className="flex items-center gap-6">
            <a
              href="/privacy"
              className="text-white/50 text-sm font-['Sora'] hover:text-[#FFDC00] transition-colors duration-150"
            >
              Privacy
            </a>
            <a
              href="/terms"
              className="text-white/50 text-sm font-['Sora'] hover:text-[#FFDC00] transition-colors duration-150"
            >
              Terms
            </a>
            <a
              href="/cookies"
              className="text-white/50 text-sm font-['Sora'] hover:text-[#FFDC00] transition-colors duration-150"
            >
              Cookies
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
